import { Stack, Typography } from "@mui/material";
import { makeTintedBackgroundFromColor } from "@entrilia/shared/utilities/colorHelper";
import { convertISODate, convertISODateTime } from "@entrilia/shared/utilities/dateUtils";
import { formatNumber } from "@entrilia/shared/utilities/formatters";
import { convertToNumber } from "@entrilia/shared/utilities/numberHelper";
import { autoFormatCamelCase, stringifyValue } from "@entrilia/shared/utilities/stringHelper";
import type { ActivityLogDetail, ActivityLogDetailValueType } from "../../../../../api/types/activityLogTypes";

const formatValue = (value: unknown, valueType: ActivityLogDetailValueType | undefined): string => {
  if (value === null || value === undefined) {
    return "";
  }

  switch (valueType) {
    case "DateOnly": {
      const stringValue = stringifyValue(value);
      return convertISODate(stringValue);
    }
    case "DateTime": {
      const stringValue = stringifyValue(value);
      return convertISODateTime(stringValue);
    }
    case "Decimal": {
      const numericValue = convertToNumber(value);
      return numericValue !== undefined ? formatNumber(numericValue) : stringifyValue(value);
    }
    case "Boolean":
    case "Text":
    case "Reference":
    case "ReferenceArray":
    default:
      return stringifyValue(value);
  }
};

interface Props {
  detail: ActivityLogDetail;
}

const hasValue = (value: unknown) => value !== undefined && value !== null && value !== "";

const ActivityRowGridDetailLine = ({ detail }: Props) => {
  if (detail.type === "ActionDetails") {
    return <Typography color="textSecondary">{detail.actionDetails}</Typography>;
  }

  if (detail.type === "Change" || detail.type === "ReferenceChange") {
    return (
      <Stack direction="row" spacing={1}>
        <Typography color="textSecondary">{detail.key}:</Typography>
        {hasValue(detail.oldValue) && (
          <Typography sx={(t) => ({ bgcolor: makeTintedBackgroundFromColor(t, t.palette.error.main) })}>
            {formatValue(detail.oldValue, detail.valueType)}
          </Typography>
        )}
        {hasValue(detail.newValue) && (
          <Typography sx={(t) => ({ bgcolor: makeTintedBackgroundFromColor(t, t.palette.success.main) })}>
            {formatValue(detail.newValue, detail.valueType)}
          </Typography>
        )}
        {detail.type === "ReferenceChange" && detail.referenceObjectType && (
          <Typography color="textSecondary">({autoFormatCamelCase(detail.referenceObjectType)})</Typography>
        )}
      </Stack>
    );
  }

  if (detail.type === "Context") {
    return (
      <Stack direction="row" spacing={1}>
        <Typography color="textSecondary">{detail.key}:</Typography>
        <Typography>{formatValue(detail.contextValue, detail.valueType)}</Typography>
        {detail.referenceObjectType && (
          <Typography color="textSecondary">({autoFormatCamelCase(detail.referenceObjectType)})</Typography>
        )}
      </Stack>
    );
  }

  return <Typography color="textSecondary">Unknown detail type</Typography>;
};

export default ActivityRowGridDetailLine;
